import type { GQL_ID, NestedFilter } from '@@partner:graphql/types'

import React, { useCallback, useMemo } from 'react'
import { prepareFeedbacksFilter } from '@@partner:helpers/feedback'

import FilterPanel from '@components/common/filter/FilterPanel'
import { createFilter, FilterConfig } from '@components/common/filter/FilterConfig'
import ShopsPicker from '@components/common/pickers/ShopsPicker'

export interface FeedbacksFiltersType {
  ids: GQL_ID[]
  shop_id?: GQL_ID[]
  orderIds: GQL_ID[]
  generalRating: (string | number)[]
  needCall: string
  hasImages?: boolean
}

export interface FeedbacksFilterProps {
  onFilterChanged: (filters: NestedFilter) => void
  /** Показывать фильтр по магазинам */
  showShopFilter?: boolean
  /** Показывать фильтр по заказам */
  showOrderFilter?: boolean
}

const AVAILABLE_FEEDBACK_RATINGS = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 }
]

const NEED_CALL_TYPES = [
  { label: 'Требуется', value: '1' },
  { label: 'Не требуется', value: '0' }
]

export default function FeedbacksFilter ({
  onFilterChanged,
  showShopFilter = false,
  showOrderFilter = true
}: FeedbacksFilterProps) {
  const defaultFilter: FeedbacksFiltersType = useMemo(() => ({
    ids: [],
    ...(showShopFilter ? { shop_id: [] } : {}),
    orderIds: [],
    generalRating: [],
    needCall: '',
    hasImages: false
  }), [ showShopFilter ])

  const filterConfig: FilterConfig[] = useMemo(() => {
    const config: FilterConfig[] = [
      createFilter.multiline('ids', 'ID отзыва по одному на строку', {
        rows: 2
      })
    ]

    if (showShopFilter) {
      config.push(
        createFilter.custom('shop_id', 'Магазин', ({ value, onChange }) => (
          <ShopsPicker
            value={value}
            onChange={onChange}
            multiple
            searchable
            placeholder="Магазин"
            size="sm"
          />
        ), { valueType: 'number[]' })
      )
    }

    if (showOrderFilter) {
      config.push(
        createFilter.multiline('orderIds', 'ID заказа по одному на строку', {
          rows: 2
        })
      )
    }

    config.push(
      createFilter.multiselect('generalRating', AVAILABLE_FEEDBACK_RATINGS, 'Рейтинг', {
        searchable: false
      }),
      createFilter.select('needCall', NEED_CALL_TYPES, 'Звонок покупателю', {
        searchable: false
      }),
      createFilter.checkbox('hasImages', 'Только с изображениями')
    )

    return config
  }, [ showShopFilter, showOrderFilter ])

  const handleSubmit = useCallback((state: FeedbacksFiltersType) => {
    onFilterChanged(prepareFeedbacksFilter(state))
  }, [ onFilterChanged ])

  return (
    <FilterPanel
      filters={filterConfig}
      defaultState={defaultFilter}
      onSearch={handleSubmit}
    />
  )
}
